import { Box, Typography } from '@mui/material'
import React from 'react'

import { PaxWiseBaggage } from '@/src/types/itinerary'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'

interface PassengerBaggageRowProps {
    label: string
    displayValue: string
    subtext: string
    isWarningInfo: boolean
    isTooltipContent: boolean
    infoNotAvailableValues: string[]
    marginTop?: boolean
}

const PassengerBaggageRow: React.FC<PassengerBaggageRowProps> = ({
    label,
    displayValue,
    subtext,
    isWarningInfo,
    isTooltipContent,
    infoNotAvailableValues,
    marginTop = false
}) => {
    if (!displayValue && !subtext) return null

    const getTextColor = () => {
        if (isWarningInfo || infoNotAvailableValues.includes(subtext)) {
            return 'error.main'
        }
        return 'text.secondary'
    }

    return (
        <Box sx={{ display: 'flex', ...(marginTop && { mt: 1 }) }}>
            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                {label}:
                {displayValue && (
                    <Typography variant="body2" component="span" sx={{ ml: 1, fontSize: '12px' }}>
                        {displayValue}
                    </Typography>
                )}
                {subtext && (
                    <Typography
                        variant="body2"
                        component="span"
                        sx={{
                            color: getTextColor(),
                            ml: 1,
                            fontSize: '12px'
                        }}
                    >
                        {subtext}
                    </Typography>
                )}
            </Typography>
        </Box>
    )
}

const BaggageBenefit = ({
    CHD,
    INF,
    onlyAdultBaggage,
    isTooltipContent,
    infoNotAvailableValues,
    adultDisplayValue,
    adultSubtext,
    isAdultBaggageWarningInfo,
    childDisplayValue,
    childSubtext,
    isChildBaggageWarningInfo,
    infantDisplayValue,
    infantSubtext,
    isInfantBaggageWarningInfo
}: {
    CHD: PaxWiseBaggage
    INF: PaxWiseBaggage
    onlyAdultBaggage: boolean
    isTooltipContent: boolean
    infoNotAvailableValues: string[]
    adultDisplayValue: string
    adultSubtext: string
    isAdultBaggageWarningInfo: boolean
    childDisplayValue: string
    childSubtext: string
    isChildBaggageWarningInfo: boolean
    infantDisplayValue: string
    infantSubtext: string
    isInfantBaggageWarningInfo: boolean
}) => {
    const passengers = [
        {
            label: 'Adult',
            displayValue: adultDisplayValue,
            subtext: adultSubtext,
            isWarningInfo: isAdultBaggageWarningInfo
        },
        {
            label: 'Child',
            displayValue: childDisplayValue,
            subtext: childSubtext,
            isWarningInfo: isChildBaggageWarningInfo
        },
        {
            label: 'Infant',
            displayValue: infantDisplayValue,
            subtext: infantSubtext,
            isWarningInfo: isInfantBaggageWarningInfo
        }
    ]

    if ((!isEmpty(CHD) || !isEmpty(INF)) && !onlyAdultBaggage) {
        return (
            <Box sx={{ whiteSpace: 'nowrap' }}>
                {passengers.map((passenger, index) => (
                    <PassengerBaggageRow
                        key={passenger.label}
                        label={passenger.label}
                        displayValue={passenger.displayValue}
                        subtext={passenger.subtext}
                        isWarningInfo={passenger.isWarningInfo}
                        isTooltipContent={isTooltipContent}
                        infoNotAvailableValues={infoNotAvailableValues}
                        marginTop={index > 0}
                    />
                ))}
            </Box>
        )
    } else {
        if (isTooltipContent) {
            return null
        }
        return (
            <Box sx={{ display: 'flex' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {(adultDisplayValue || adultSubtext) && (
                        <Typography
                            variant="body2"
                            sx={{ fontSize: '12px', fontWeight: 'inherit', textAlign: 'right' }}
                        >
                            {adultDisplayValue}
                            {adultSubtext && (
                                <Typography
                                    variant="body2"
                                    component="span"
                                    marginLeft={0.5}
                                    sx={{
                                        color:
                                            infoNotAvailableValues.includes(adultSubtext) || isAdultBaggageWarningInfo
                                                ? 'error.main'
                                                : 'text.secondary',
                                        fontSize: '12px',
                                        fontWeight: 'inherit'
                                    }}
                                >
                                    {adultSubtext}
                                </Typography>
                            )}
                        </Typography>
                    )}
                </Box>
            </Box>
        )
    }
}

export default BaggageBenefit
